import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Dices, Scissors, Shapes, SlidersHorizontal } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { SHAPE_MAP, buildShape, fmt } from "../lib/sewingShapes";
import { JOINT_MAP } from "../lib/sewingComposites";
import { labHref } from "../lib/sewingLinks";
import {
  CREATURE_DIALS,
  buildCreature,
  creatureBlurb,
  defaultCreature,
  randomCreature,
} from "../lib/sewingCreature";
import FoldViewer from "../components/sewing/FoldViewer";
import PatternPieces from "../components/sewing/PatternPieces";

/* ============================================================
   /sewing-primitives/creature — the creature generator.

   The curriculum's thesis, cashed in: semantic sliders (chubby,
   leggy, big-eared) resolve into primitive parameters, and the
   cut list below is a real, sewable pattern for whatever you
   dial in. The attachment points are computed from the body's
   actual surface, so no slider setting can break the plush.
   ============================================================ */

// One semantic dial: choice params render as pills, numbers as sliders.
const Dial = ({ dial, value, onChange }) => {
  if (dial.type === "choice") {
    return (
      <div className="text-sm">
        <span className="font-medium capitalize">{dial.label}</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {dial.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(dial.key, opt.value)}
              aria-pressed={value === opt.value}
              className={
                value === opt.value
                  ? "rounded-full border border-primary bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary"
                  : "rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground"
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <label className="block text-sm">
      <span className="flex items-baseline justify-between">
        <span className="font-medium capitalize">{dial.label}</span>
        <span className="tabular-nums text-muted-foreground">
          {fmt(value)}
          {dial.unit}
        </span>
      </span>
      <input
        type="range"
        min={dial.min}
        max={dial.max}
        step={dial.step}
        value={value}
        onChange={(e) => onChange(dial.key, Number(e.target.value))}
        className="mt-1 w-full accent-primary"
        aria-label={dial.label}
      />
    </label>
  );
};

const SewingCreaturePage = () => {
  const [dials, setDials] = useState(defaultCreature);
  const setDial = (key, value) => setDials((d) => ({ ...d, [key]: value }));

  const model = useMemo(() => buildCreature(dials), [dials]);

  // Group identical parts (arms, legs, ears) into one cut-list entry each.
  const groups = useMemo(() => {
    const byKey = new Map();
    model.parts.forEach((part) => {
      const key = `${part.name}|${part.shape}|${JSON.stringify(part.params)}`;
      if (byKey.has(key)) byKey.get(key).count += 1;
      else byKey.set(key, { ...part, count: 1 });
    });
    return [...byKey.values()];
  }, [model]);

  const jointsUsed = [...new Set(model.attachments.map((a) => a.joint))].map(
    (id) => JOINT_MAP[id]
  );
  const standsTall = Math.max(...model.positionsAt(1).map((p) => p[1]));

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
      />
      <div className="container relative mx-auto max-w-5xl px-4 py-14">
        <Link
          to="/sewing-primitives/make"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          back to make
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-4xl" aria-hidden>
            🪄
          </span>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            <span className="text-gold-shimmer">Creature generator</span>
          </h1>
        </div>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          The whole curriculum, cashed in. The dials speak personality —
          chubby, leggy, big-eared — and the math translates: a lathe body, a
          gored head, cylinder limbs, joints computed onto the body's actual
          surface. The cut list below is real. You could sew this exact
          creature.
        </p>
        <p className="mt-2 text-sm font-medium text-primary">
          Currently on the table: {creatureBlurb(dials)}.
        </p>

        <div className="mt-8 grid gap-5 lg:grid-cols-[1.2fr,1fr]">
          <Card className="bg-card/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Your creature — parts ↔ assembled</CardTitle>
              <CardDescription>{model.insight}</CardDescription>
            </CardHeader>
            <CardContent>
              <FoldViewer
                model={model}
                labels={{
                  start: "apart",
                  end: "assembled",
                  button: "put it together",
                  aria: "Assemble the creature from its parts",
                  sceneAria:
                    "3D view of a generated plush creature assembling from primitive parts",
                }}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-5">
            <Card className="bg-card/60 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <SlidersHorizontal className="h-4 w-4 text-primary" />
                  Personality dials
                </CardTitle>
                <CardDescription>
                  Stands ~{fmt(standsTall)} cm tall. Every dial recomputes every
                  pattern below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {CREATURE_DIALS.map((d) => (
                  <Dial key={d.key} dial={d} value={dials[d.key]} onChange={setDial} />
                ))}
                <button
                  type="button"
                  onClick={() => setDials(randomCreature(Math.random))}
                  className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-primary/50 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20"
                >
                  <Dices className="h-4 w-4" />
                  surprise me
                </button>
              </CardContent>
            </Card>

            <Card className="bg-card/60 backdrop-blur">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Joints used here</CardTitle>
                <CardDescription>
                  Dot colors in the 3D view — where and how parts attach.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {jointsUsed.map((joint) => (
                  <div key={joint.id} className="flex items-start gap-2.5 text-sm">
                    <span
                      aria-hidden
                      className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border border-white/60"
                      style={{ backgroundColor: joint.color }}
                    />
                    <div>
                      <p className="font-semibold">{joint.name}</p>
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {model.attachments
                          .filter((a) => a.joint === joint.id)
                          .map((a) => a.label)
                          .join(" · ")}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Cut list: the generated, sewable pattern for the current dials. */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Scissors className="h-6 w-6 text-primary" />
            What you'd cut
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Generated from the dials above — nudge one and watch the numbers
            follow. Seam allowance is not included; add ~0.7 cm everywhere.
            Note the ears sew in flattened (see the teddy lesson) and the arms
            embed at the shoulder.
          </p>
          <div className="mt-6 space-y-8">
            {groups.map((group) => (
              <div key={`${group.name}-${group.shape}-${group.id}`}>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold capitalize">{group.name}</h3>
                  <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-primary">
                    make {group.count}
                  </span>
                  <Link
                    to={labHref(group.shape, group.params)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <Shapes className="h-3.5 w-3.5" />
                    open {SHAPE_MAP[group.shape].name.toLowerCase()} in the lab
                  </Link>
                  {group.note && (
                    <span className="text-xs text-muted-foreground">— {group.note}</span>
                  )}
                </div>
                <PatternPieces model={buildShape(group.shape, group.params)} />
              </div>
            ))}
          </div>
        </section>

        <p className="mt-14 text-center text-sm text-muted-foreground">
          Prefer a plan someone already tested? The{" "}
          <Link to="/sewing-primitives/make" className="text-primary hover:underline">
            starter menu
          </Link>{" "}
          has seven. 🧵
        </p>
      </div>
    </div>
  );
};

export default SewingCreaturePage;
